import { type FC, useEffect } from 'react';
import { useCallback, useMemo } from 'react';

import { Box, Select, SelectItem, SelectItemText, Text } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

import { useClipAudioTracks } from '../../hooks/useClipAudioTracks';
import { useClipTranslations } from '../../hooks/useClipTranslations';
import { useLanguageNamesForAudiotracks } from '../../hooks/useLanguageNamesForAudiotracks';
import type { TSelectItem } from '../../types';
import { TRACKED_ERRORS, trackError, trackingHandle } from '../../utils/datadog';
import { TryAgainAlert } from '../TryAgainAlert';
import { AUDIO_TRACKS_TYPES } from './translations';

export const VALUE_ORIGINAL_AUDIO_TRACK = 'VALUE_ORIGINAL_AUDIO_TRACK';

const DISABLED_SX = {
  cursor: 'not-allowed',
  opacity: 0.5,
  pointerEvents: 'none',
};

type Props = {
  selectedAudioTrack: string;
  hasEditRights: boolean;
  setSelectedAudioTrack: (selected: TSelectItem) => void;
  videoID?: string;
  hasVideoTranslations: boolean;
  versionId?: number;
  videoLanguage?: string | null;
};
export const AudioTrackSelector: FC<Props> = ({
  videoID,
  hasEditRights,
  setSelectedAudioTrack,
  selectedAudioTrack,
  hasVideoTranslations,
  versionId,
  videoLanguage,
}) => {
  const { languageCodeToNameMap } = useLanguageNamesForAudiotracks();

  const { keepPollingTranslations, translations } = useClipTranslations({
    videoId: videoID,
    hasVideoTranslations,
  });
  const { accessibleAudioTracks, isLoading, getAudioTracks, audioTracksError } = useClipAudioTracks(
    {
      videoId: videoID,
      videoVersionId: versionId,
      keepPollingTranslations,
      translations,
      exclusiveAccess: hasEditRights,
    },
  );

  const handleRetryGetAudioTracks = useCallback(() => {
    trackingHandle.audioTrackSelector.userRetriesGetAudiotracks();
    getAudioTracks();
  }, [getAudioTracks]);

  useEffect(() => {
    if (audioTracksError) {
      trackError(audioTracksError, {
        videoID,
        hint: "Failed to fetch clip's alternate audio tracks.",
        method: 'useGetVideoVersionAudiotracks',
        trackingTag: TRACKED_ERRORS.FETCH_AUDIOTRACKS_FAILED,
      });
    }
  }, [audioTracksError, videoID]);

  const optionsFromFetchedTracks = useMemo(() => {
    return accessibleAudioTracks
      .map((track): TSelectItem => {
        // 'commentary' | 'descriptive' | 'main' | 'unknown'
        const audioTrackSourceName = AUDIO_TRACKS_TYPES[track.type] ?? track.type;
        const isAIGenerated = track.provenance === 'ai_generated';
        const audioTrackPostfix = isAIGenerated ? `(${AUDIO_TRACKS_TYPES['ai_generated']})` : '';
        const audioTrackLanguage = `${track.language.name}`;

        return {
          label: [audioTrackLanguage, audioTrackSourceName, audioTrackPostfix].join(' '),
          value: track.uri,
          disableReason: track.disableReason,
          isAIGenerated: isAIGenerated,
          isOriginalAudio: false,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [accessibleAudioTracks]);

  const originalLangLabel =
    videoLanguage && languageCodeToNameMap ? languageCodeToNameMap[videoLanguage] : videoLanguage;

  const multiSelectItems = useMemo((): TSelectItem[] => {
    const originalOption: TSelectItem = {
      label: originalLangLabel
        ? `${originalLangLabel} (${translate('original')})`
        : translate('Original audio track'),
      value: VALUE_ORIGINAL_AUDIO_TRACK,
      isAIGenerated: false,
      isOriginalAudio: true,
    };
    return [originalOption].concat(optionsFromFetchedTracks);
  }, [optionsFromFetchedTracks, originalLangLabel]);

  const selectAudioTrack = useCallback(
    (details: { value: string[]; items: unknown[] }) => {
      const pickedValue = details.value[0];
      const optionThatWereSelected = multiSelectItems.find((item) => item.value === pickedValue);

      setSelectedAudioTrack({
        value: pickedValue,
        label: optionThatWereSelected?.label ?? pickedValue,
        isAIGenerated: !!optionThatWereSelected?.isAIGenerated,
        isOriginalAudio: !!optionThatWereSelected?.isOriginalAudio,
      });
    },
    [multiSelectItems, setSelectedAudioTrack],
  );

  const onSelectToggle = useCallback(({ open }: { open: boolean }) => {
    if (open) {
      trackingHandle.audioTrackSelector.userOpenAudiotracksPopover();
    } else {
      trackingHandle.audioTrackSelector.userCloseAudiotracksPopover();
    }
  }, []);

  if (optionsFromFetchedTracks.length === 0 && !audioTracksError) {
    return null;
  }

  if (audioTracksError) {
    trackingHandle.audioTrackSelector.onSelectorRetryRendered();
  } else {
    trackingHandle.audioTrackSelector.onSelectorUIRendered();
  }

  return (
    <Box>
      <Text variant="heading-xs" mb="md">
        {translate('Audio language')}
      </Text>
      <Select
        value={selectedAudioTrack ? [selectedAudioTrack] : undefined}
        items={multiSelectItems}
        disabled={isLoading}
        placeholder={translate('Original audio track')}
        size="md"
        onValueChange={selectAudioTrack}
        highlightedValue={selectedAudioTrack}
        onOpenChange={onSelectToggle}
      >
        {(item: TSelectItem) => (
          <SelectItem
            mt="sm"
            display="flex"
            gap="sm"
            alignItems="center"
            sx={item.disableReason ? DISABLED_SX : undefined}
          >
            <SelectItemText>{item.label}</SelectItemText>
          </SelectItem>
        )}
      </Select>
      {audioTracksError && <TryAgainAlert onRetry={handleRetryGetAudioTracks} />}
    </Box>
  );
};
